// PayoutForm.jsx - The last thing a customer does: choose how to be paid, show ID,
// and sign. It is one screen because it is one moment for them, even though it
// covers three different compliance requirements.
//
// Photographs go straight to a data URI in the browser and are downscaled before
// upload, because a modern phone camera produces a 12MB file for a driving licence
// and nobody on a rural connection should wait for that.
//
// LEGEND
//   1. METHODS        payout options and the fields each one needs
//   2. useImageInput  file to downscaled data URI
//   3. SignaturePad   canvas signature, with a typed alternative
//   4. PayoutForm     the form

import { useCallback, useEffect, useRef, useState } from "react";

import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { useForm } from "../lib/hooks.js";
import { money } from "../lib/format.js";
import { Button, Input, RadioCards, Checkbox, Alert, Field } from "../ui/kit.jsx";
import { CONSENT, requiredAt } from "../lib/legal.js";
import { Camera, Check, Shield, Close, ArrowUpRight } from "../ui/icons.jsx";

////////// 1. METHODS //////////
// `fields` drives the form, so adding a payout method is one entry here.

const METHODS = [
  {
    value: "ach", label: "Bank transfer", hint: "One to two business days",
    fields: [
      { name: "accountName", label: "Name on the account", required: true, autoComplete: "name" },
      { name: "bankName", label: "Bank name", required: true },
      { name: "routingNumber", label: "Routing number", required: true, inputMode: "numeric", maxLength: 9 },
      { name: "accountNumber", label: "Account number", required: true, inputMode: "numeric" },
    ],
  },
  {
    value: "zelle", label: "Zelle", hint: "Usually within minutes",
    fields: [{ name: "zelleIdentifier", label: "Zelle phone number or email", required: true }],
  },
  {
    value: "cashapp", label: "Cash App", hint: "Usually within minutes",
    fields: [{ name: "cashAppTag", label: "Your $Cashtag", required: true, placeholder: "$yourtag" }],
  },
  {
    value: "paypal", label: "PayPal", hint: "Usually within an hour",
    fields: [{ name: "payPalEmail", label: "PayPal email", required: true, type: "email" }],
  },
  {
    value: "check", label: "Paper check", hint: "Three to five days by mail",
    fields: [{ name: "mailTo", label: "Mail the check to", required: true, placeholder: "Leave blank to use your kit address" }],
  },
];

////////// 2. IMAGE INPUT //////////

const MAX_EDGE = 1600;

function readAsDownscaledDataUri(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => resolve(reader.result); // not an image we can draw, send as is
      image.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height));
        if (scale === 1 && String(reader.result).length < 2_000_000) return resolve(reader.result);

        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        return resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function PhotoInput({ label, hint, value, onChange, required }) {
  const [busy, setBusy] = useState(false);

  const handle = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      onChange(await readAsDownscaledDataUri(file));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label={label} hint={hint} required={required}>
      {value ? (
        <div className="relative border border-rule rounded-xs overflow-hidden bg-surface">
          <img src={value} alt={label} className="w-full h-36 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-ink/80 text-paper p-1.5 rounded-xs hover:bg-ink"
            aria-label={`Remove ${label}`}
          >
            <Close size={15} />
          </button>
          <span className="absolute bottom-2 left-2 tag tag-positive"><Check size={12} /> Added</span>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center gap-2 h-36 border border-dashed border-rule rounded-xs cursor-pointer hover:border-bronze hover:bg-bronze-wash/40 transition-colors text-muted">
          <Camera size={22} className={busy ? "spin" : ""} />
          <span className="text-[13px] font-medium">{busy ? "Preparing" : "Take a photo or choose a file"}</span>
          <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={handle} />
        </label>
      )}
    </Field>
  );
}

////////// 3. SIGNATURE //////////

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);

  // The canvas is sized to its rendered box times the device pixel ratio, or the
  // signature looks like it was drawn with a marker on a phone.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const box = canvas.getBoundingClientRect();
    canvas.width = box.width * ratio;
    canvas.height = box.height * ratio;
    const context = canvas.getContext("2d");
    context.scale(ratio, ratio);
    context.lineWidth = 1.8;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#1b1a17";
  }, []);

  const pointFrom = (event) => {
    const box = canvasRef.current.getBoundingClientRect();
    const source = event.touches?.[0] || event;
    return { x: source.clientX - box.left, y: source.clientY - box.top };
  };

  const start = (event) => {
    event.preventDefault();
    drawing.current = true;
    const context = canvasRef.current.getContext("2d");
    const point = pointFrom(event);
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const context = canvasRef.current.getContext("2d");
    const point = pointFrom(event);
    context.lineTo(point.x, point.y);
    context.stroke();
    setEmpty(false);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange("");
  };

  return (
    <div>
      <div className="relative border border-rule bg-card rounded-xs">
        <canvas
          ref={canvasRef}
          className="w-full h-32 touch-none cursor-crosshair block"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {empty && (
          <span className="absolute inset-0 flex items-center justify-center text-[13px] text-faint pointer-events-none">
            Sign here with your finger or mouse
          </span>
        )}
        <div className="absolute bottom-0 left-6 right-6 border-b border-rule-soft pointer-events-none" style={{ bottom: 26 }} />
      </div>
      <button type="button" onClick={clear} className="btn btn-ghost btn-sm mt-1">Clear</button>
    </div>
  );
}

////////// 4. FORM //////////

export default function PayoutForm({ token, order, onDone }) {
  const [method, setMethod] = useState("ach");
  const [signatureType, setSignatureType] = useState("draw");
  const [signatureImage, setSignatureImage] = useState("");
  const [idFront, setIdFront] = useState("");
  const [idBack, setIdBack] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);

  const form = useForm({ details: {}, signatureText: "" });
  const chosen = METHODS.find((entry) => entry.value === method);

  const setDetail = useCallback((name, value) => {
    form.setValues((previous) => ({ ...previous, details: { ...previous.details, [name]: value } }));
  }, [form]);

  const submit = form.submit(async (values) => {
    await publicApi.submitPayout(token, {
      method,
      details: values.details,
      // What was actually ticked, not what the code assumes was ticked. The
      // submit button is already gated on both, so this changes nothing today,
      // and it means the record can never say yes because of a literal.
      ownershipDeclared: agreed,
      acceptedPurchaseAgreement: acceptedAgreement,
      signatureType,
      signatureText: signatureType === "type" ? values.signatureText : undefined,
      signatureImage: signatureType === "draw" ? signatureImage : undefined,
      idFront: idFront || undefined,
      idBack: idBack || undefined,
    });
    analytics.trackPayoutSubmitted(values.method);
    onDone?.();
    return true;
  });

  const ready =
    agreed &&
    acceptedAgreement &&
    (signatureType === "draw" ? Boolean(signatureImage) : (form.values.signatureText || "").trim().length > 2) &&
    chosen.fields.filter((field) => field.required).every((field) => String(form.values.details[field.name] || "").trim());

  return (
    <form onSubmit={submit} className="space-y-7" noValidate>
      <div>
        <h3 className="display display-sm text-ink mb-1">How would you like the {money(order.offer?.amount)}?</h3>
        <p className="hint mb-4">Pick whichever is easiest. You can change it by replying to any of our messages.</p>
        <RadioCards
          name="payout-method"
          options={METHODS.map(({ value, label, hint }) => ({ value, label, hint }))}
          value={method}
          onChange={(next) => { setMethod(next); form.setValues((previous) => ({ ...previous, details: {} })); }}
          columns={2}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {chosen.fields.map((field) => (
          <Input
            key={field.name}
            label={field.label}
            required={field.required}
            type={field.type}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            autoComplete={field.autoComplete}
            value={form.values.details[field.name] || ""}
            onChange={(event) => setDetail(field.name, event.target.value)}
            error={form.errors[`details.${field.name}`]}
            wrapClassName={chosen.fields.length === 1 ? "sm:col-span-2" : undefined}
          />
        ))}
      </div>

      <div className="border-t border-rule pt-7">
        <h3 className="display display-sm text-ink mb-1">A photo of your ID</h3>
        <p className="hint mb-4 measure">
          Precious metals dealers are required to verify who they buy from. Your images are encrypted and
          are never shared outside our business.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <PhotoInput label="Front of your ID" value={idFront} onChange={setIdFront} />
          <PhotoInput label="Back of your ID" value={idBack} onChange={setIdBack} />
        </div>
        <p className="hint mt-2">If you do not have it to hand, submit without it and send it later. We will remind you.</p>
      </div>

      <div className="border-t border-rule pt-7">
        <h3 className="display display-sm text-ink mb-1">Sign</h3>
        <p className="hint mb-4">Confirming the items were yours to sell, which is the same thing you would sign in a shop.</p>

        <div className="flex gap-1 mb-3">
          {[["draw", "Draw it"], ["type", "Type it"]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setSignatureType(value)}
              className={`btn btn-sm ${signatureType === value ? "btn-ink" : "btn-quiet"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {signatureType === "draw" ? (
          <SignaturePad value={signatureImage} onChange={setSignatureImage} />
        ) : (
          <Input
            label="Type your full legal name"
            placeholder="Jane Q. Public"
            className="display text-xl"
            {...form.field("signatureText")}
          />
        )}

        <div className="mt-5 space-y-4">
          <Checkbox
            label="These items are mine to sell, and everything I have told you is true."
            hint="Recorded with the date, time and your network address, the same as a signed slip in a shop."
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
          />

          {/* The bill of sale is the document that moves ownership, so it gets
              its own box rather than being folded into the one above. Two
              separate things are being agreed and the record says so. */}
          <div className="panel-quiet p-4">
            <Checkbox
              label={CONSENT.purchase}
              checked={acceptedAgreement}
              onChange={(event) => setAcceptedAgreement(event.target.checked)}
            />
            <ul className="mt-3.5 pt-3.5 border-t border-rule flex flex-wrap gap-x-5 gap-y-1.5">
              {requiredAt("purchase").map((doc) => (
                <li key={doc.key}>
                  <a
                    href={`/legal/${doc.key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink link"
                  >
                    Read it <ArrowUpRight size={13} className="text-bronze" />
                  </a>
                  <span className="hint ml-1.5">v{doc.version}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {form.message && <Alert tone="error">{form.message.text}</Alert>}

      <div className="flex flex-wrap items-center gap-4 pt-1">
        <Button type="submit" size="lg" loading={form.busy} disabled={!ready}>
          {form.busy ? "Sending" : "Submit and get paid"}
        </Button>
        <p className="hint flex items-center gap-1.5">
          <Shield size={15} className="text-bronze" /> Encrypted in transit and at rest
        </p>
      </div>
    </form>
  );
}
